/* eslint-disable no-useless-escape */
import { useState, useMemo, useEffect } from 'react';
import { Row, Col, Container, Button } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { Table, Faq, HubspotModal } from '@mdc/ui';
import { useTranslation } from 'react-i18next';
import DatesDropdown from '@mdc/ui/src/top-leaderboards/DatesDropdown';
import DetectionReport from '@mdc/ui/src/outbreak/DetectionReport';
import { i18nUtility, QueryParams, TranslationFix } from '@mdc/services';
import { HUBSPOT_PORTAL_ID, HUBSPOT_FORMS, ROUTES } from '@mdc/constants';


import './OutbreakReport.scss';

const OutbreakReport = (props) => {
    const { faqs, availableDates, hashes, detections, updateDate, latestDate } = props;
    const { date } = QueryParams();
    const [show, setShow] = useState(false);

    const { t, ready } = useTranslation();

    const language = i18nUtility.lookupLang().replace(/-/g, '_');
    const form = HUBSPOT_FORMS[language];

    const TABLE_COLUMNS = [
        {
            Header: t('FILE'),
            accessor: 'fileName'
        },
        {
            Header: t('CLOUD*'),
            accessor: 'cloud'
        },
        {
            Header: t('8 Engines'),
            accessor: 'engines8',
        },
        {
            Header: t('12 Engines'),
            accessor: 'engines12'
        },
        {
            Header: t('16 Engines'),
            accessor: 'engines16'
        },
        {
            Header: t('20 Engines'),
            accessor: 'engines20'
        },
        {
            Header: t('MAX Engines'),
            accessor: 'enginesMax'
        }
    ];

    const trimString = (day) => {
        if (day && day.includes('outbreak-report/')) {
            return day.replace(/outbreak-report\//, '');
        }

        return day;
    };

    const availableDatesTemp = availableDates && availableDates.map((day) => trimString(day));
    const currentReportDate = date && date.toLowerCase() === 'latest' ? 'Latest' : date;

    useEffect(() => {
        window.history.replaceState('', '', `?date=${typeof date !== 'undefined' ? date : 'Latest'}`);
    }, []);

    const handleClick = () => setShow(true);
    const handleClose = () => setShow(false);

    const highlightFirstDetection = (item) => {
        return <span className="highlighted">{item}</span>;
    };

    const faqFooter =  <TranslationFix
        message={t('Learn more with a demonstration of the technology, or to receive additional information please <a href=\"https://www.opswat.com/contact\" rel=\"noopener noreferrer\" target=\"_blank\">Contact Us</a>')}
        type='p'
    />;

    const tableData = useMemo(() => {
        return hashes.length > 0 ? hashes.map((item) => {
            const cloud = item.detection.mcl_detection_time;
            let engines8 = item.detection.m8_detection_time || <i className="icon-undetected-threat notDetectedIcon"/>;
            let engines12 = item.detection.m12_detection_time || <i className="icon-undetected-threat notDetectedIcon"/>;
            let engines16 = item.detection.m16_detection_time || <i className="icon-undetected-threat notDetectedIcon"/>;
            let engines20 = item.detection.m20_detection_time || <i className="icon-undetected-threat notDetectedIcon"/>;
            let enginesMax = item.detection.mmax_detection_time || <i className="icon-undetected-threat notDetectedIcon"/>;

            const fileName = (<a href={`${ROUTES.results.href}/file/${item.sha1}/hash/history`} target="_blank" rel="noopener noreferrer">{item.display_name}</a>);

            const integerValues = [];
            typeof engines8 === 'number' && integerValues.push(engines8);
            typeof engines12 === 'number' && integerValues.push(engines12);
            typeof engines16  === 'number' && integerValues.push(engines16);
            typeof engines20 === 'number' && integerValues.push(engines20);
            typeof enginesMax === 'number' && integerValues.push(enginesMax);

            const minValue = Math.min(...integerValues);

            return {
                fileName: fileName,
                cloud: cloud,
                engines8: engines8 === minValue ? highlightFirstDetection(engines8) : engines8,
                engines12: engines12 === minValue ? highlightFirstDetection(engines12) : engines12,
                engines16: engines16 === minValue ? highlightFirstDetection(engines16) : engines16,
                engines20: engines20 === minValue ? highlightFirstDetection(engines20) : engines20,
                enginesMax: enginesMax === minValue ? highlightFirstDetection(enginesMax) : enginesMax,
            };
        }) : [];
    }, [hashes]);

    const dropDownSelect = <Col className="align-self-center text-right p-0">
        <span className="dateGroup">
            <DatesDropdown
                queryDate={currentReportDate}
                availableDates={availableDatesTemp}
                updateDate={updateDate}
            />
        </span>
    </Col>;

    const tableLegend = <TranslationFix
        message={t('Time is shown in hours. Outbreaks as detected from executables & PE files ONLY. <br/> To learn more please review our <a href=\"https://onlinehelp.opswat.com/mdcloud/2.1_Scanning_a_file_by_file_upload.html\" rel=\"noopener noreferrer\" target=\"_blank\">Multiscanning Documentation</a>')}
        type='p'
        className="legend"
    />;

    const tableFooter = useMemo(() => {
        const tableLength = tableData.length > 0 ? tableData.length : '';
        return <Row className="tableFooter">
            <Col xs={12} sm={12} md={6} lg={6} className="mb-4">
                <p>{t('The latest {{tableLength}} outbreaks are reported above. The full report contains over 2000 entries and is available in CVS format. To request the report, click the button and enter your email address, the CVS file will be sent to you', { tableLength })}</p>
                <Button variant="dark" className="buttons darkButton" onClick={handleClick}>{t('Request Report')}</Button>
            </Col>

            <Col xs={12} sm={12} md={6} lg={6}>
                <ul className="tableSymbolsLegend">
                    <li>{t('MetaDefender On-premise Engine version that first detected the threat')}</li>
                    <li>{t('Cloud version uses different engines in comparison to on-premise versions of Metadefender')}</li>
                    <li>{t('Indicates the threat was not detected by the specific version of MetaDefender')}</li>
                    <li dangerouslySetInnerHTML={{ __html:
                    t('Email address are used in accordance with our <a href=\"https://onlinehelp.opswat.com/policies/OPSWAT_Privacy_Policy.html\" rel=\"noopener noreferrer\" target=\"_blank\">Email Marketing Policy</a>') }}>
                    </li>
                </ul>
            </Col>
        </Row>;
    }, [tableData, t]);

    if (!ready) {
        return null;
    }

    return (
        <Container className="outbreak">
            <section className="top">
                <Row>
                    <div className="topWrapper d-flex justify-content-between">
                        {dropDownSelect}
                    </div>
                </Row>
            </section>

            <DetectionReport tableLegend={tableLegend} data={detections} currentReportDate={currentReportDate} availableDates={availableDates} latestDate={latestDate}/>
            <section className="timeToDetection">
                <h1>{t('MetaDefender Time-to-Detection Report')}</h1>
                <Table
                    useSorting={false}
                    footerInfoBox={tableLegend}
                    rowsPerPage={10}
                    data={tableData}
                    columns={TABLE_COLUMNS}/>
                {tableFooter}
            </section>

            <Faq faqs={faqs} faqFooter={faqFooter} faqTitle={t('Malware Detection FAQs')}></Faq>

            <HubspotModal
                portalId={HUBSPOT_PORTAL_ID}
                formId={form.OUTBREAK_FORM_ID}
                show={show}
                handleClose={handleClose}
                title={t('Request outbreak CSV file')}
            ></HubspotModal>

        </Container>
    );
};

OutbreakReport.propTypes = {
    faqs: PropTypes.array,
    availableDates: PropTypes.array,
    hashes: PropTypes.array,
    detections: PropTypes.array,
    updateDate: PropTypes.func,
    latestDate: PropTypes.string
};

OutbreakReport.defaultProps = {
    faqs: []
};

export default OutbreakReport;
